import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import './EntityInput.css';

/**
 * EntityInput - A rich text input component with entity autocomplete.
 * 
 * Features:
 * - Inline entity badges that are inserted into the text
 * - TAB key to accept the first suggestion
 * - Arrow keys to navigate suggestions
 * - Enter to accept highlighted suggestion
 * - Debounced search queries sent to Dash callbacks
 */
export default class EntityInput extends Component {
    constructor(props) {
        super(props);
        this.inputRef = createRef();
        this.debounceTimer = null;
        // Monotonic counter to correlate search requests with responses.
        // Incremented on every searchQuery dispatch; stale responses are
        // discarded by comparing against the counter in componentDidUpdate.
        this._searchId = 0;
        this._lastDispatchedQuery = '';
        this.state = {
            highlightedIndex: 0,
            showSuggestions: false,
            currentQuery: '',
            cursorPosition: null,
        };
    }

    componentDidMount() {
        // Focus the input on mount if autoFocus is set
        if (this.props.autoFocus && this.inputRef.current) {
            this.inputRef.current.focus();
        }
    }

    /**
     * Highlight the matching portion of a suggestion label.
     * Wraps matched substrings in <strong> to show why the result matched.
     */
    /**
     * Stopwords to exclude from matching / highlighting.
     */
    static STOPWORDS = new Set([
        'a','an','the','for','of','in','on','to','and','or',
        'is','are','was','were','be','been','with','at','by',
        'from','as','it','its','that','this','what','which',
        'who','how','have','has','do','does','can','will',
    ]);

    highlightMatch = (label) => {
        const { currentQuery } = this.state;
        if (!currentQuery || currentQuery.length < 2) return label;

        // Split query into meaningful words (drop stopwords & short)
        const queryWords = currentQuery
            .toLowerCase()
            .split(/\s+/)
            .filter(w => w.length >= 2 && !EntityInput.STOPWORDS.has(w));

        if (queryWords.length === 0) return label;

        // Build a regex that matches any query word (prefix-style)
        const escaped = queryWords.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
        const re = new RegExp(`(${escaped.join('|')})`, 'gi');

        // Split the label around matches and wrap matched portions in <strong>
        const parts = label.split(re);
        if (parts.length === 1) return label; // no match

        return (
            <React.Fragment>
                {parts.map((part, i) =>
                    re.test(part) ? <strong key={i}>{part}</strong> : part
                )}
            </React.Fragment>
        );
    };

    /**
     * Client-side relevance filter: discard suggestions whose label
     * shares no meaningful word-prefix with the current query.
     * This catches stale responses that slipped through the async round-trip.
     */
    _filterRelevantSuggestions = (suggestions) => {
        const { currentQuery } = this.state;
        if (!currentQuery || currentQuery.length < 2 || !suggestions || suggestions.length === 0) {
            return suggestions || [];
        }

        const queryWords = currentQuery
            .toLowerCase()
            .split(/\s+/)
            .filter(w => w.length >= 2 && !EntityInput.STOPWORDS.has(w));

        if (queryWords.length === 0) return suggestions;

        return suggestions.filter(entity => {
            const label = (entity.label || '').toLowerCase();
            // At least one query word must appear as a substring in the label
            return queryWords.some(qw => label.includes(qw));
        });
    };

    componentDidUpdate(prevProps) {
        // Reset highlighted index when suggestions change
        if (prevProps.suggestions !== this.props.suggestions) {
            // Only show suggestions if there is an active search query
            const hasActiveQuery = this.state.currentQuery && this.state.currentQuery.length >= 2;

            // Staleness guard: verify suggestions still match the current input.
            // The Dash callback is async — by the time suggestions arrive the
            // user may have typed something completely different.
            if (hasActiveQuery && this.props.suggestions && this.props.suggestions.length > 0) {
                const relevant = this._filterRelevantSuggestions(this.props.suggestions);
                this.setState({
                    highlightedIndex: 0,
                    showSuggestions: relevant.length > 0,
                });
            } else {
                this.setState({
                    highlightedIndex: 0,
                    showSuggestions: false,
                });
            }
        }

        // Apply analysis results when they arrive
        if (
            this.props.analysisResults &&
            this.props.analysisResults !== prevProps.analysisResults &&
            this.props.analysisResults.length > 0
        ) {
            this.applyAnalysisResults(this.props.analysisResults);
        }

        // Inject an externally-set query string into the contentEditable DOM.
        // This handles cases where Dash pushes a new `query` value (e.g. when an
        // example question is clicked), which would otherwise be ignored because
        // the contentEditable div manages its own DOM.
        if (
            this.props.query !== prevProps.query &&
            this.inputRef.current
        ) {
            const currentDomText = this.getFullQuery();
            // Only overwrite when the incoming prop differs from what's already
            // rendered, to avoid clobbering the user's own edits that triggered
            // the prop update via updateState().
            if (this.props.query !== currentDomText) {
                // Set plain text content (clears any existing badges/nodes)
                this.inputRef.current.textContent = this.props.query || '';
                // Move cursor to end
                if (this.props.query) {
                    const range = document.createRange();
                    const sel = window.getSelection();
                    range.selectNodeContents(this.inputRef.current);
                    range.collapse(false);
                    sel.removeAllRanges();
                    sel.addRange(range);
                }
            }
        }
    }

    /**
     * Get the plain text content from the input (excluding entity badges)
     */
    getPlainText = () => {
        if (!this.inputRef.current) return '';
        
        let text = '';
        this.inputRef.current.childNodes.forEach(node => {
            if (node.nodeType === Node.TEXT_NODE) {
                text += node.textContent;
            } else if (node.classList && node.classList.contains('entity-badge')) {
                // Entity badges contribute their label to the text
                text += ' ';
            }
        });
        return text.trim();
    };

    /**
     * Get the full composed query with entity badges formatted as markdown
     * entity links: [label](type:id).
     *
     * This format is understood by the Chat messageRenderer which converts
     * these links into styled entity badge chips, and by the backend which
     * uses the embedded type:id to resolve concepts.
     */
    toMarkdownLinkType = (entityType) => {
        const map = {
            taxonomy_resource: 'resource',
            ontology_type: 'edge',
            taxonomy_type: 'type',
            concept: 'concept',
            node: 'node',
            edge: 'edge',
        };
        return map[entityType] || entityType || 'node';
    };

    getFullQuery = () => {
        if (!this.inputRef.current) return '';
        
        let text = '';
        this.inputRef.current.childNodes.forEach(node => {
            if (node.nodeType === Node.TEXT_NODE) {
                text += node.textContent;
            } else if (node.classList && node.classList.contains('entity-badge')) {
                // Format entity as markdown link: [label](type:id)
                const label = node.dataset.entityLabel || '';
                const id = node.dataset.entityId || '';
                const type = this.toMarkdownLinkType(node.dataset.entityType || 'node');
                text += `[${label}](${type}:${id})`;
            }
        });
        // Normalize whitespace: collapse multiple spaces into one
        return text.replace(/\s+/g, ' ').trim();
    };

    /**
     * Get the display query with entity labels inline (no markdown).
     * Used internally for positional operations like applyAnalysisResults
     * where offsets refer to the user-visible text.
     */
    getDisplayQuery = () => {
        if (!this.inputRef.current) return '';
        
        let text = '';
        this.inputRef.current.childNodes.forEach(node => {
            if (node.nodeType === Node.TEXT_NODE) {
                text += node.textContent;
            } else if (node.classList && node.classList.contains('entity-badge')) {
                const label = node.dataset.entityLabel || '';
                text += label;
            }
        });
        return text.replace(/\s+/g, ' ').trim();
    };

    /**
     * Get all selected entities as an array in order of appearance.
     * Iterates through childNodes to preserve DOM order.
     */
    getSelectedEntities = () => {
        if (!this.inputRef.current) return [];
        
        const entities = [];
        // Iterate through childNodes to preserve order of appearance
        this.inputRef.current.childNodes.forEach(node => {
            if (node.classList && node.classList.contains('entity-badge')) {
                entities.push({
                    id: node.dataset.entityId,
                    label: node.dataset.entityLabel,
                    type: node.dataset.entityType,
                });
            }
        });
        return entities;
    };

    /**
     * Get the current word being typed (for autocomplete)
     */
    getCurrentWord = () => {
        const selection = window.getSelection();
        if (!selection.rangeCount) return { word: '', startOffset: 0, endOffset: 0 };
        
        const range = selection.getRangeAt(0);
        if (range.startContainer.nodeType !== Node.TEXT_NODE) {
            return { word: '', startOffset: 0, endOffset: 0 };
        }
        
        const text = range.startContainer.textContent;
        const cursorPos = range.startOffset;
        
        // Find word boundaries
        let start = cursorPos;
        let end = cursorPos;
        
        while (start > 0 && !/\s/.test(text[start - 1])) {
            start--;
        }
        while (end < text.length && !/\s/.test(text[end])) {
            end++;
        }
        
        return {
            word: text.substring(start, cursorPos),
            startOffset: start,
            endOffset: end,
            textNode: range.startContainer,
        };
    };

    /**
     * Get the current phrase being typed (up to 3 trailing words).
     * Used for multi-word entity matching: when the user types "Data Eng",
     * the phrase is "Data Eng" so the server can match "Data Engineering".
     */
    getCurrentPhrase = () => {
        const selection = window.getSelection();
        if (!selection.rangeCount) return { phrase: '', word: '', phraseStartOffset: 0, wordStartOffset: 0, textNode: null };
        
        const range = selection.getRangeAt(0);
        if (range.startContainer.nodeType !== Node.TEXT_NODE) {
            return { phrase: '', word: '', phraseStartOffset: 0, wordStartOffset: 0, textNode: null };
        }
        
        const text = range.startContainer.textContent;
        const cursorPos = range.startOffset;
        const beforeCursor = text.substring(0, cursorPos);
        
        // Current word: last non-whitespace run
        const wordMatch = beforeCursor.match(/(\S+)$/);
        const word = wordMatch ? wordMatch[1] : '';
        const wordStartOffset = cursorPos - word.length;
        
        // Phrase: last 1–3 words before cursor
        const phraseMatch = beforeCursor.match(/(?:\S+\s+){0,2}\S+$/);
        const phrase = phraseMatch ? phraseMatch[0].trim() : word;
        const phraseStartOffset = phraseMatch
            ? cursorPos - phraseMatch[0].trimStart().length
            : wordStartOffset;
        
        return { phrase, word, phraseStartOffset, wordStartOffset, textNode: range.startContainer };
    };

    /**
     * Insert an entity badge at the current cursor position.
     * Uses phrase-aware replacement: if the phrase (up to 3 words) is a
     * prefix-match of the entity label, the whole phrase is replaced;
     * otherwise only the current word is replaced.
     */
    insertEntity = (entity) => {
        const { setProps, selectedEntities = [] } = this.props;
        const { phrase, word, phraseStartOffset, wordStartOffset, textNode } = this.getCurrentPhrase();
        
        if (!textNode || !this.inputRef.current) return;
        
        // Decide how much text to replace
        let replaceStart = wordStartOffset;
        let replaceLen = word.length;
        
        if (phrase.length > word.length) {
            // Check if phrase words are prefix-matches of entity label words
            const phraseWords = phrase.toLowerCase().split(/\s+/);
            const entityWords = entity.label.toLowerCase().split(/\s+/);
            let matchesPhrase = phraseWords.length <= entityWords.length;
            if (matchesPhrase) {
                for (let i = 0; i < phraseWords.length; i++) {
                    if (!entityWords[i].startsWith(phraseWords[i])) {
                        matchesPhrase = false;
                        break;
                    }
                }
            }
            // Also accept if the entity label simply contains the full phrase
            if (!matchesPhrase && entity.label.toLowerCase().includes(phrase.toLowerCase())) {
                matchesPhrase = true;
            }
            if (matchesPhrase) {
                replaceStart = phraseStartOffset;
                replaceLen = phrase.length;
            }
        }
        
        // Create the entity badge element
        const badge = document.createElement('span');
        badge.className = `entity-badge entity-type-${entity.type || 'default'}`;
        badge.contentEditable = 'false';
        badge.dataset.entityId = entity.id;
        badge.dataset.entityLabel = entity.label;
        badge.dataset.entityType = entity.type || 'node';
        // Trusted Types safe: use DOM API instead of innerHTML
        const labelSpan = document.createElement('span');
        labelSpan.className = 'entity-label';
        labelSpan.textContent = entity.label;
        const removeSpan = document.createElement('span');
        removeSpan.className = 'entity-remove';
        removeSpan.title = 'Remove';
        removeSpan.textContent = '\u00d7';
        badge.appendChild(labelSpan);
        badge.appendChild(removeSpan);
        
        // Add click handler for remove button
        removeSpan.addEventListener('click', (e) => {
            e.stopPropagation();
            badge.remove();
            this.updateState();
        });
        
        // Replace the typed text with the badge
        const beforeText = textNode.textContent.substring(0, replaceStart);
        let afterText = textNode.textContent.substring(replaceStart + replaceLen);

        // Phase 2 fix: trim leftover word fragments that could pollute the
        // next getCurrentPhrase() call.  If the remaining text starts with
        // non-whitespace characters that were part of the word the user was
        // typing (i.e. not separated by a space), strip them.
        afterText = afterText.replace(/^\S*/, '').trimStart();

        // Create text nodes
        const beforeNode = document.createTextNode(beforeText);
        const afterNode = document.createTextNode(' ' + afterText);
        
        // Replace the text node with before + badge + after
        const parent = textNode.parentNode;
        parent.insertBefore(beforeNode, textNode);
        parent.insertBefore(badge, textNode);
        parent.insertBefore(afterNode, textNode);
        parent.removeChild(textNode);
        
        // Set cursor after the badge
        const range = document.createRange();
        const sel = window.getSelection();
        range.setStart(afterNode, 1);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
        
        // Update state
        this.setState({ showSuggestions: false, currentQuery: '' });
        this.updateState();
    };

    /**
     * Update Dash props with current state
     */
    updateState = () => {
        const { setProps } = this.props;
        const plainText = this.getPlainText();
        const fullQuery = this.getFullQuery();
        const entities = this.getSelectedEntities();
        
        setProps({
            // New naming convention - fullQuery is the complete text with entities
            query: fullQuery,
            keywords: entities,
            // Backward compatibility
            value: plainText,
            selectedEntities: entities,
            searchQuery: '',
        });
    };

    /**
     * Handle input changes with debounced search.
     * Sends the multi-word phrase (up to 3 words) instead of just the current
     * word so the server can match multi-word entity names.
     *
     * Uses adaptive debounce: shorter delay (150ms) for short terms where
     * the user is likely still typing a single word, longer delay (300ms)
     * for phrases to batch keystrokes.
     */
    handleInput = (e) => {
        const { setProps, debounceMs = 300 } = this.props;
        
        // Clear existing timer
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }
        
        const { phrase, word } = this.getCurrentPhrase();
        // Use the phrase for search if multi-word, else fall back to word
        const searchTerm = phrase.length > word.length ? phrase : word;
        
        // Update value immediately
        const plainText = this.getPlainText();
        const fullQuery = this.getFullQuery();
        const entities = this.getSelectedEntities();
        setProps({
            // New naming convention - fullQuery is the complete text with entities
            query: fullQuery,
            keywords: entities,
            // Backward compatibility
            value: plainText,
            selectedEntities: entities,
        });
        
        // Debounce the search query
        if (searchTerm.length >= 2) {
            // If the search term changed significantly (different first word
            // compared to last dispatched query), eagerly hide stale suggestions
            const prevFirstWord = (this._lastDispatchedQuery || '').split(/\s+/)[0]?.toLowerCase();
            const currFirstWord = searchTerm.split(/\s+/)[0]?.toLowerCase();
            if (prevFirstWord && currFirstWord && !currFirstWord.startsWith(prevFirstWord) && !prevFirstWord.startsWith(currFirstWord)) {
                // First word diverged — old suggestions are irrelevant
                setProps({ suggestions: [] });
            }

            this.setState({ currentQuery: searchTerm, showSuggestions: true });

            // Adaptive debounce: faster for short single words, slower for phrases
            const adaptiveDelay = searchTerm.length <= 4 ? Math.min(debounceMs, 150) : debounceMs;
            this.debounceTimer = setTimeout(() => {
                this._searchId += 1;
                this._lastDispatchedQuery = searchTerm;
                setProps({ searchQuery: searchTerm });
            }, adaptiveDelay);
        } else {
            this.setState({ showSuggestions: false, currentQuery: '' });
            this._lastDispatchedQuery = '';
            setProps({ searchQuery: '', suggestions: [] });
        }
    };

    /**
     * Handle keyboard events
     */
    handleKeyDown = (e) => {
        const { suggestions = [] } = this.props;
        const { highlightedIndex, showSuggestions } = this.state;

        // Use the same relevance filter applied in render() so keyboard
        // navigation indexes match the visible dropdown items.
        const filtered = showSuggestions ? this._filterRelevantSuggestions(suggestions) : [];
        
        // Only handle special keys when suggestions are showing
        if (showSuggestions && filtered.length > 0) {
            switch (e.key) {
                case 'Tab':
                    // TAB accepts the first/highlighted suggestion
                    e.preventDefault();
                    this.insertEntity(filtered[highlightedIndex]);
                    break;
                    
                case 'Enter':
                    // Enter accepts the highlighted suggestion
                    e.preventDefault();
                    this.insertEntity(filtered[highlightedIndex]);
                    break;
                    
                case 'ArrowDown':
                    e.preventDefault();
                    this.setState({
                        highlightedIndex: Math.min(highlightedIndex + 1, filtered.length - 1)
                    });
                    break;
                    
                case 'ArrowUp':
                    e.preventDefault();
                    this.setState({
                        highlightedIndex: Math.max(highlightedIndex - 1, 0)
                    });
                    break;
                    
                case 'Escape':
                    e.preventDefault();
                    // Clear query state completely so re-focus doesn't re-show stale dropdown
                    this.setState({ showSuggestions: false, currentQuery: '' });
                    this._lastDispatchedQuery = '';
                    this.props.setProps && this.props.setProps({ searchQuery: '', suggestions: [] });
                    break;
                    
                default:
                    break;
            }
        } else if (e.key === 'Enter' && !e.shiftKey) {
            // Submit on Enter when no suggestions
            e.preventDefault();
            const { setProps, onSubmit } = this.props;
            const plainText = this.getPlainText();
            const fullQuery = this.getFullQuery();
            const entities = this.getSelectedEntities();
            setProps({
                // New naming convention - fullQuery is the complete text with entities
                query: fullQuery,
                keywords: entities,
                // Backward compatibility
                value: plainText,
                selectedEntities: entities,
                submitted: true,
            });
        } else if (e.key === 'Backspace') {
            // Check if we're about to delete an entity badge
            const selection = window.getSelection();
            if (selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);
                if (range.collapsed && range.startOffset === 0) {
                    // Check if previous sibling is a badge
                    const prevSibling = range.startContainer.previousSibling;
                    if (prevSibling && prevSibling.classList && prevSibling.classList.contains('entity-badge')) {
                        e.preventDefault();
                        prevSibling.remove();
                        this.updateState();
                    }
                }
            }
        }
    };

    /**
     * Handle suggestion click
     */
    handleSuggestionClick = (entity, e) => {
        e.preventDefault();
        e.stopPropagation();
        this.inputRef.current.focus();
        this.insertEntity(entity);
    };

    /**
     * Handle analyze button click - increment counter to trigger Dash callback
     */
    handleAnalyzeClick = () => {
        const { setProps, analyzeClicked = 0 } = this.props;
        setProps({ analyzeClicked: analyzeClicked + 1 });
    };

    /**
     * Apply analysis results: replace matched text spans with entity badges.
     * Processes matches sorted by position (longest first for overlaps).
     */
    applyAnalysisResults = (results) => {
        if (!this.inputRef.current || !results || results.length === 0) return;

        // Flatten all match positions: [{id, label, type, start, end}, ...]
        const allPositions = [];
        results.forEach(entity => {
            if (entity.positions && entity.positions.length > 0) {
                entity.positions.forEach(pos => {
                    allPositions.push({
                        id: entity.id,
                        label: entity.name || entity.label || entity.matched_text,
                        type: entity.entity_type || entity.type || 'node',
                        matchedText: entity.matched_text || entity.label,
                        start: pos.start,
                        end: pos.end,
                    });
                });
            }
        });

        if (allPositions.length === 0) return;

        // Sort by start descending so we can replace from end to start without shifting offsets
        allPositions.sort((a, b) => b.start - a.start);

        // Remove overlaps (keep longer match)
        const filtered = [];
        for (const pos of allPositions) {
            const overlaps = filtered.some(
                f => !(pos.end <= f.start || pos.start >= f.end)
            );
            if (!overlaps) filtered.push(pos);
        }

        // Get current plain text (display version without markdown links,
        // so positional offsets from the analysis endpoint match correctly)
        const text = this.getDisplayQuery();
        if (!text) return;

        // Build new content: split text by match positions and interleave badges
        // Sort ascending for building
        filtered.sort((a, b) => a.start - b.start);

        // Clear input
        this.inputRef.current.innerHTML = '';

        let lastEnd = 0;
        filtered.forEach(pos => {
            // Add text before this match
            if (pos.start > lastEnd) {
                const textBefore = text.substring(lastEnd, pos.start);
                this.inputRef.current.appendChild(document.createTextNode(textBefore));
            }

            // Create entity badge
            const badge = document.createElement('span');
            badge.className = `entity-badge entity-type-${pos.type || 'default'}`;
            badge.contentEditable = 'false';
            badge.dataset.entityId = pos.id;
            badge.dataset.entityLabel = pos.label;
            badge.dataset.entityType = pos.type;

            const labelSpan = document.createElement('span');
            labelSpan.className = 'entity-label';
            labelSpan.textContent = pos.label;
            const removeSpan = document.createElement('span');
            removeSpan.className = 'entity-remove';
            removeSpan.title = 'Remove';
            removeSpan.textContent = '\u00d7';
            badge.appendChild(labelSpan);
            badge.appendChild(removeSpan);

            removeSpan.addEventListener('click', (e) => {
                e.stopPropagation();
                badge.remove();
                this.updateState();
            });

            this.inputRef.current.appendChild(badge);
            lastEnd = pos.end;
        });

        // Add remaining text
        if (lastEnd < text.length) {
            this.inputRef.current.appendChild(document.createTextNode(text.substring(lastEnd)));
        }

        // Add trailing space for cursor
        this.inputRef.current.appendChild(document.createTextNode(' '));

        // Update state
        this.updateState();

        // Clear analysisResults to avoid re-processing
        const { setProps } = this.props;
        setProps({ analysisResults: [] });
    };

    /**
     * Handle paste - strip formatting
     */
    handlePaste = (e) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain');
        document.execCommand('insertText', false, text);
    };

    render() {
        const { 
            id, 
            placeholder, 
            suggestions = [],
            className = '',
            disabled = false,
        } = this.props;
        const { highlightedIndex, showSuggestions } = this.state;

        return (
            <div className={`entity-input-container ${className}`}>
                <div className="entity-input-row">
                    <div
                        ref={this.inputRef}
                        id={id}
                        className={`entity-input ${disabled ? 'disabled' : ''}`}
                        contentEditable={!disabled}
                        onInput={this.handleInput}
                        onKeyDown={this.handleKeyDown}
                        onPaste={this.handlePaste}
                        onBlur={() => {
                            // Delay hiding to allow click on suggestions
                            setTimeout(() => this.setState({ showSuggestions: false }), 200);
                        }}
                        onFocus={() => {
                            if (this.state.currentQuery.length >= 2) {
                                this.setState({ showSuggestions: true });
                            }
                        }}
                        data-placeholder={placeholder}
                        suppressContentEditableWarning={true}
                    />
                    <button
                        className="entity-analyze-btn"
                        type="button"
                        title="Analyze entire text for entities"
                        onClick={this.handleAnalyzeClick}
                        disabled={disabled}
                        aria-label="Analyze text"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M15 4V2" />
                            <path d="M15 16v-2" />
                            <path d="M8 9h2" />
                            <path d="M20 9h2" />
                            <path d="M17.8 11.8L19 13" />
                            <path d="M15 9h0" />
                            <path d="M17.8 6.2L19 5" />
                            <path d="M3 21l9-9" />
                            <path d="M12.2 6.2L11 5" />
                        </svg>
                    </button>
                </div>
                
                {showSuggestions && suggestions.length > 0 && (() => {
                    const filtered = this._filterRelevantSuggestions(suggestions);
                    return filtered.length > 0 ? (
                    <div className="entity-suggestions">
                        {filtered.map((entity, index) => (
                            <div
                                key={entity.id}
                                className={`entity-suggestion ${index === highlightedIndex ? 'highlighted' : ''}`}
                                onMouseDown={(e) => this.handleSuggestionClick(entity, e)}
                                onMouseEnter={() => this.setState({ highlightedIndex: index })}
                            >
                                <span className={`entity-type-icon entity-type-${entity.type || 'default'}`}>
                                    {EntityInput.TYPE_ICONS[entity.type] || EntityInput.TYPE_ICONS.default}
                                </span>
                                <span className="entity-suggestion-label">
                                    {this.highlightMatch(entity.label)}
                                </span>
                                {entity.description && (
                                    <span className="entity-suggestion-desc">{entity.description}</span>
                                )}
                                <span className="entity-suggestion-hint">TAB</span>
                            </div>
                        ))}
                    </div>
                    ) : null;
                })()}
            </div>
        );
    }
}

EntityInput.defaultProps = {
    placeholder: 'Type to search entities...',
    suggestions: [],
    // New naming convention
    query: '',
    keywords: [],
    // Backward compatibility
    selectedEntities: [],
    value: '',
    searchQuery: '',
    debounceMs: 300,
    autoFocus: false,
    disabled: false,
    submitted: false,
    analyzeClicked: 0,
    analysisResults: [],
};

/**
 * Icon glyphs keyed by entity type.
 * Shared between render() and highlightMatch so the mapping lives in one place.
 */
EntityInput.TYPE_ICONS = {
    node: '●',
    taxonomy_resource: '◆',
    ontology_type: '⬡',
    concept: '◇',
    edge: '↔',
    default: '○',
};

EntityInput.propTypes = {
    /**
     * The ID used to identify this component in Dash callbacks.
     */
    id: PropTypes.string,

    /**
     * Placeholder text shown when input is empty.
     */
    placeholder: PropTypes.string,

    /**
     * The current plain text query (without entity badges).
     * Primary output - use this for the search query.
     */
    query: PropTypes.string,

    /**
     * Array of currently selected keywords/entities in the input.
     * Primary output - use this for entity context.
     * Each keyword has: { id, label, type }
     */
    keywords: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        label: PropTypes.string,
        type: PropTypes.string,
    })),

    /**
     * The current plain text value (without entity badges).
     * @deprecated Use `query` instead
     */
    value: PropTypes.string,

    /**
     * Array of currently selected entities in the input.
     * Each entity has: { id, label, type }
     * @deprecated Use `keywords` instead
     */
    selectedEntities: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        label: PropTypes.string,
        type: PropTypes.string,
    })),

    /**
     * The current search query (the word being typed for autocomplete).
     * This is updated after debounce and triggers Dash callback to fetch suggestions.
     */
    searchQuery: PropTypes.string,

    /**
     * Array of entity suggestions to display.
     * Each suggestion has: { id, label, type, description? }
     */
    suggestions: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        type: PropTypes.string,
        description: PropTypes.string,
    })),

    /**
     * Debounce delay in milliseconds for search queries.
     */
    debounceMs: PropTypes.number,

    /**
     * Whether to auto-focus the input on mount.
     */
    autoFocus: PropTypes.bool,

    /**
     * Whether the input is disabled.
     */
    disabled: PropTypes.bool,

    /**
     * Additional CSS class names.
     */
    className: PropTypes.string,

    /**
     * Set to true when user presses Enter (for submission).
     */
    submitted: PropTypes.bool,

    /**
     * Counter incremented when the analyze button (magic wand) is clicked.
     * Use this in Dash callbacks to trigger full-text entity analysis.
     */
    analyzeClicked: PropTypes.number,

    /**
     * Array of analysis results from the /analyze endpoint.
     * When set, matched entities are inserted as badges into the input.
     * Each result has: { id, name, matched_text, entity_type, positions: [{start, end}] }
     */
    analysisResults: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string,
        label: PropTypes.string,
        matched_text: PropTypes.string,
        entity_type: PropTypes.string,
        type: PropTypes.string,
        positions: PropTypes.arrayOf(PropTypes.shape({
            start: PropTypes.number.isRequired,
            end: PropTypes.number.isRequired,
        })),
    })),

    /**
     * Dash-assigned callback that should be called to report property changes
     * to Dash, to make them available for callbacks.
     */
    setProps: PropTypes.func,
};
